import React, { useState, useRef, useEffect, useCallback } from 'react';

const OPENING_MESSAGE = { role: 'assistant', content: "Hey! I'm Archie. What are you planning today? Tell me about your class and I'll help you brainstorm." };
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

const ArchieChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([OPENING_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const inactivityTimer = useRef(null);

  const resetChat = useCallback(() => {
    setMessages([OPENING_MESSAGE]);
    setInput('');
  }, []);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      resetChat();
    }, INACTIVITY_TIMEOUT);
  }, [resetChat]);

  useEffect(() => {
    return () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    resetInactivityTimer();

    try {
      const response = await fetch('/api/archie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: updatedMessages })
      });

      const data = await response.json();
      const reply = data.reply;

      if (reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      }
    } catch (error) {
      console.error('Archie error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having a moment! Try again in a second." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (text) => {
    return text.split('\n').filter(line => line.trim()).map((line, i) => (
      <p key={i} style={{ margin: i === 0 ? 0 : '8px 0 0 0' }}>{line}</p>
    ));
  };

  return (
    <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 1000 }}>

      {/* Closed state */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: '#D85A30', color: 'white', border: 'none',
            borderRadius: '24px', padding: '10px 16px 10px 10px',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <img
            src="/images/archie.png"
            alt="Archie"
            width={32}
            height={32}
            style={{ borderRadius: '50%', objectFit: 'cover' }}
          />
          <span style={{ fontSize: '13px', fontWeight: 500 }}>Chat with Archie</span>
        </button>
      )}

      {/* Open state */}
      {open && (
        <div style={{
          width: '440px', background: 'white',
          border: '0.5px solid rgba(0,0,0,0.15)',
          borderRadius: '12px', overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
        }}>

          {/* Header */}
          <div style={{
            background: '#D85A30', padding: '12px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img
                src="/images/archie.png"
                alt="Archie"
                width={28}
                height={28}
                style={{ borderRadius: '50%', objectFit: 'cover' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: 500, color: 'white' }}>Archie</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)' }}>Your personal teaching assistant</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={resetChat}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white', cursor: 'pointer', fontSize: '12px',
                  padding: '4px 10px', borderRadius: '12px'
                }}
              >
                New chat
              </button>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '20px', lineHeight: 1, padding: '2px 4px' }}
              >
                −
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{
            padding: '12px', height: '460px', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: '10px'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', padding: '8px 12px', fontSize: '13px', lineHeight: 1.6,
                  borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                  background: msg.role === 'user' ? '#D85A30' : '#FAECE7',
                  color: msg.role === 'user' ? 'white' : '#4A1B0C',
                  textAlign: 'left'
                }}>
                  {formatMessage(msg.content)}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: '#FAECE7', borderRadius: '12px 12px 12px 2px',
                  padding: '8px 14px', fontSize: '18px', color: '#D85A30'
                }}>
                  ···
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            borderTop: '0.5px solid rgba(0,0,0,0.1)', padding: '10px 12px',
            display: 'flex', gap: '8px', alignItems: 'flex-end'
          }}>
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Archie anything..."
              rows={1}
              style={{
                flex: 1, border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: '12px',
                padding: '7px 12px', fontSize: '13px', outline: 'none',
                background: '#fafafa', color: '#333', resize: 'none',
                lineHeight: 1.5, maxHeight: '120px',
                overflowY: input.split('\n').length > 5 ? 'auto' : 'hidden'
              }}
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              style={{
                background: input.trim() && !loading ? '#D85A30' : '#ccc',
                border: 'none', borderRadius: '50%', width: '32px', height: '32px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                flexShrink: 0, marginBottom: '2px'
              }}
            >
              <svg width="14" height="14" fill="none" stroke="white" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchieChat;